import React, { useState, useRef, useEffect } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";

const VideoUploadStep = ({
  step,
  title,
  exampleVideoUrl,
  instructions,
  uploadedUrl,
  notes,
  onUpload,
  onBack,
}) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(null); // { type: "error" | "success", text: "" }
  const fileInputRef = useRef(null);
  const previousStep = useRef(step);

  // Reset on step change
  useEffect(() => {
    if (previousStep.current !== step) {
      setFile(null);
      setPopup(null);
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      previousStep.current = step;
    }
  }, [step]);

  const handleUpload = async () => {
    if (!file && !uploadedUrl) {
      setPopup({
        type: "error",
        text: "Please select a video file or use the saved one.",
      });
      return;
    }

    setLoading(true);
    try {
      if (file) {
        await onUpload(step, file); // Upload and proceed
      } else {
        // No file selected but uploadedUrl exists, so just proceed to next step
        setPopup({ type: "success", text: "Using previously uploaded video." });
        // Trigger parent to move to next step without upload
        await onUpload(step, null);
      }
    } catch (err) {
      console.error(err);
      setPopup({ type: "error", text: "Upload failed. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Auto-close popup
  useEffect(() => {
    if (popup) {
      const timer = setTimeout(() => setPopup(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [popup]);

  return (
    <div className="max-w-3xl mx-auto p-4 bg-white rounded shadow">
      <button
        onClick={onBack}
        className="flex items-center mb-4 text-blue-600"
        disabled={loading}
      >
        <ArrowLeft className="mr-1" /> Back
      </button>

      <h2 className="text-xl font-bold mb-4">{title}</h2>

      {exampleVideoUrl && (
        <div className="mb-4">
          <p className="font-semibold">Example Video</p>
          <video width="320" controls className="rounded my-2">
            <source src={exampleVideoUrl} type="video/mp4" />
          </video>
        </div>
      )}

      <div className="mb-4">
        <h3 className="font-semibold text-lg">Instructions</h3>
        <ul className="list-disc pl-6 text-gray-700">
          {instructions.map((text, idx) => (
            <li key={idx}>{text}</li>
          ))}
        </ul>
      </div>

      {notes && (
        <div className="bg-yellow-100 p-3 rounded text-sm mb-4">
          <strong>Note:</strong> {notes}
        </div>
      )}

      {/* Tailwind popup */}
      {popup && (
        <div
          className={`mb-4 p-3 rounded text-sm flex items-center gap-2 shadow-md ${
            popup.type === "success"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {popup.type === "success" ? "✅" : "⚠️"} {popup.text}
        </div>
      )}

      <input
        type="file"
        accept="video/*"
        ref={fileInputRef}
        disabled={loading}
        onChange={(e) => setFile(e.target.files[0])}
        className="mb-3"
      />

      <button
        onClick={handleUpload}
        disabled={loading}
        className={`px-4 py-2 rounded text-white ${
          loading
            ? "bg-blue-400 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700"
        }`}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="animate-spin" size={18} />
            Uploading...
          </span>
        ) : (
          "Upload Video"
        )}
      </button>
      {uploadedUrl && (
        <div className="mt-4">
          <p className="text-sm font-medium mb-1 text-gray-700">
            Previously Uploaded Video
          </p>
          <video
            src={uploadedUrl}
            controls
            className="w-full max-w-md rounded border"
          />
        </div>
      )}
    </div>
  );
};

export default VideoUploadStep;
